import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import type { AnalyticsData, AnalyticsSummary } from '@/lib/api'
import { ChangeBadge } from './change-badge'
import { fmt, fmtCompact } from './utils'

export function HeroSection({
  summary,
  previous,
  timeSeries,
  isLoading,
}: {
  summary: AnalyticsSummary
  previous: AnalyticsSummary | null
  timeSeries: AnalyticsData['timeSeries']
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-12 w-48" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-16 w-48" />
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-12 w-24" />
              <Skeleton className="h-12 w-24" />
              <Skeleton className="h-12 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Sparkline data: resultadoLiquido over time
  const sparklineData = timeSeries.map((t) => ({
    month: t.month,
    value: t.receita1aCompra + t.receitaRecompras - t.custoReal,
  }))

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          {/* Main metric */}
          <div className="flex-1">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Resultado Líquido
            </p>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-kpi">{fmt(summary.resultadoLiquido)}</span>
              {previous && (
                <ChangeBadge
                  current={summary.resultadoLiquido}
                  previous={previous.resultadoLiquido}
                />
              )}
            </div>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-lg font-semibold text-primary font-display">
                ROI {summary.roi}x
              </span>
              {previous && <ChangeBadge current={summary.roi} previous={previous.roi} />}
            </div>
          </div>

          {/* Sparkline */}
          {sparklineData.length > 1 && (
            <div className="w-48 h-16 hidden md:block">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparklineData}>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Sub-metrics */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-6 mt-6 lg:mt-0">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Receita
              </p>
              <p className="text-lg font-bold font-display mt-0.5">
                {fmtCompact(summary.receitaTotal)}
              </p>
              {previous && (
                <ChangeBadge current={summary.receitaTotal} previous={previous.receitaTotal} />
              )}
            </div>
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Custo Real
              </p>
              <p className="text-lg font-bold font-display mt-0.5">
                {fmtCompact(summary.custoReal)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Passivo
              </p>
              <p className="text-lg font-bold font-display mt-0.5">
                {fmtCompact(summary.passivoPendente + summary.passivoDisponivel)}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
